<template>
<transition name="dialog-fade" @after-enter="showContent = true">
	<div
		v-if="modelValue"
		data-component="BaseDialog.vue"
		class="prompt-dialog | !max-w-full fixed inset-0 grid items-end sm:items-center bg-black/50 z-2048"
		@click="dismiss('overlay')"
	>
		<transition name="dialog-slide" @after-leave="showContent = false">
			<div
				v-if="showContent"
				class="prompt-dialog-content | relative bg-white w-full mx-auto flex flex-col max-h-full overflow-hidden"
				:class="[contentStyle[type], contentClass]"
				@click.stop
			>
				<div
					v-if="isWindow"
					class="shrink-0 flex items-center gap-2"
					:class="ScreenSize < 1 ? 'py-4 px-5 border-neutral-100 border-b shadow-sm' : 'pt-6 pb-10 px-6 justify-between'"
				>
					<template v-if="ScreenSize >= 1">
						<div class="heading-5" v-html="windowTitle"/>
						<BaseIcon v-if="displayClose" :data-test="dataTest && `${dataTest}__close`" class="text-neutral-700 cursor-pointer p-[10px]" name="close" :size="2" @click="dismiss('window')"/>
					</template>
					<template v-else>
						<BaseIcon v-if="displayBack" class="p-1" name="arrow-left" :size="1.5" @click="$emit('go-back')"/>
						<div class="font-bold text-base" v-html="windowTitle"/>
						<div class="grow"/>
						<BaseIcon v-if="displayClose" :data-test="dataTest && `${dataTest}__close`" class="p-2" name="close" :size="1.5" @click="dismiss('window')"/>
					</template>
				</div>
				<div v-else-if="displayClose" class="sm:mb-4 flex justify-end mb-5 -mx-2">
					<BaseIcon class="text-neutral-700 cursor-pointer p-[10px]" :data-test="dataTest && `${dataTest}__close`" name="close" :size="2" @click="dismiss('window')"/>
				</div>

				<div class="grow" :class="{ 'px-4 lg:px-6 overflow-y-auto': isWindow }">
					<slot/>
				</div>
				<div v-if="$slots.footer" class="bg-white shadow-default py-3 px-4 lg:px-6 sticky w-full">
					<slot name="footer"/>
				</div>
			</div>
		</transition>
	</div>
</transition>
</template>

<script lang="ts">
import { PropType } from 'vue';

type BaseDialogType = 'default' | 'window' | 'large';

/*
	Okno dialogowe.
*/
export default {
	name: 'BaseDialog',
	props: {
		/**
		 * Czy modal ma być wyświetlony?
		 */
		modelValue: {
			type: Boolean,
			default: false,
		},

		/**
		 * Zamiast aktualizować model przy wywołaniu akcji, wywołaj `window.history.back()`.
		 */
		historyMode: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy zamknąć okno po kliknięciu na nakładkę?
		 */
		closeOnOverlay: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy wyświetlić przycisk do zamknięcia?
		 */
		displayClose: {
			type: Boolean,
			default: true,
		},

		/**
		 * Czy wyświetlić przycisk wywołujący akcję cofnięcia (jeżeli okno posiada tytuł i jesteśmy na mobilce)?
		 */
		displayBack: {
			type: Boolean,
			default: true,
		},

		/**
		 * Tytuł okna dialogowego
		 */
		windowTitle: {
			type: String,
			default: '',
		},

		/**
		 * Jakiego stylu użyć dla okna dialogowego?
		 */
		type: {
			type: String as PropType<BaseDialogType>,
			default: 'default',
		},

		/*
 		* Klasy, które powinny być zastosowane do wewnętrznego kontenera
 		*/
		contentClass: {
			type: String,
			default: '',
		},
		/**
		 * Atrybut data test potrzebny testerom
		 */
		dataTest: {
			type: String,
			default: undefined,
		},

	},
	emits: ['update:modelValue', 'dismissed', 'opened', 'closed', 'go-back'],
	data() {
		return {
			/**
			 * Pokaż zawartość okna (po zakończeniu animacji nakładki)
			 */
			showContent: this.modelValue,

			/** Styl okna dialogowego */
			contentStyle: {
				default: 'max-w-[564px] rounded-t-lg sm:rounded-b-lg text-center sm:text-left py-6 px-8',
				window: 'max-w-[600px] sm:max-h-[600px] sm:rounded-lg h-full',
				large: 'max-w-[760px] rounded-t-lg sm:rounded-b-lg text-center sm:text-left py-6 px-8',
			},
		};
	},

	computed: {
		isWindow() {
			return this.type === 'window';
		},
	},

	watch: {
		/**
		 * Podczas zmiany stanu wyświetlenia okna, wyślij odpowiedni event o stanie otwarcia dialogu
		 */
		modelValue(value:boolean) {
			if (value) {
				this.$emit('opened');
			} else {
				this.$emit('closed');
			}
		},
	},

	methods: {
		/**
		 * Po kliknięciu przycisku wysyła odpowiednie zdarzenie (`dismissed`)
		 * @param source Źródło kliknięcia (przycisk, nakładka)
		 */
		dismiss(source:'window'|'overlay') {
			if (source === 'overlay' && !this.closeOnOverlay) { return; }
			this.$emit('dismissed', source);
			this.close();
		},

		/**
		 * Zamyka okno dialogowe: w zależności od właściwościwości `historyMode`, ustawia
		 */
		close() {
			this.showContent = false;
			if (this.historyMode) {
				window.history.back();
			} else {
				this.$emit('update:modelValue', false);
			}
		},
	},
};
</script>

<style>
.dialog-fade-enter-active,
.dialog-fade-leave-active {
	transition: opacity 0.15s ease-in-out 0.15s;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
	opacity: 0;
}
.dialog-slide-enter-active,
.dialog-slide-leave-active {
	transition: all 0.3s ease;
}

.dialog-slide-enter-from,
.dialog-slide-leave-to {
	opacity: 0;
	transform: translateY(4rem);
}
</style>
